import { getScrollTop, scrollToTopOf } from './scroll';

export interface IScrollSpyFragment<T> {
  name: T;
  node: HTMLElement;
  offset: number;
}

type FragmentPosositions<T extends string> = {
  [key in T]: { top: number; bottom: number };
};

interface IScrollerConstructorOptions {
  disableNativeScrollRestoration: boolean;
}

export class Scroller<T extends string> {
  private fragments: IScrollSpyFragment<T>[] = [];
  private activeFragmentAfterScroll: T;
  private isScrolling = false;
  private cachedFragmentsPositions: FragmentPosositions<T> | null;
  private clearCacheTimeout: number;
  private options: IScrollerConstructorOptions;
  private defaultOptions: IScrollerConstructorOptions = {
    disableNativeScrollRestoration: false,
  };

  public constructor(options?: Partial<IScrollerConstructorOptions>) {
    this.options = {
      ...this.defaultOptions,
      ...options,
    };
  }

  public registerFragment(fragmentConfig: IScrollSpyFragment<T>) {
    if (this.options.disableNativeScrollRestoration && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.fragments.push({
      name: fragmentConfig.name,
      node: fragmentConfig.node,
      offset: fragmentConfig.offset,
    });
  }

  public scrollTo(name: T, duration: number = 500): Promise<void> {
    const target = this.fragments.find(f => f.name === name);
    if (!target) {
      return Promise.resolve();
    }

    this.activeFragmentAfterScroll = name;
    this.isScrolling = true;

    return scrollToTopOf(target.node, target.offset, duration).then(() => {
      setTimeout(() => (this.isScrolling = false), 100);

      return;
    });
  }

  public fragmentInViewport(name: T): boolean {
    if (this.isScrolling) {
      return name === this.activeFragmentAfterScroll;
    }

    const fragmentsPositions = this.getFragmentsPosition();
    const scrollTop = getScrollTop();
    const viewportHeight = window.innerHeight;
    const scrollBottom = scrollTop + viewportHeight;

    const fragmentPartViewport = (fragment: IScrollSpyFragment<T>) => {
      const { top, bottom } = fragmentsPositions[fragment.name];
      if (top > scrollBottom || bottom < scrollTop) {
        return 0;
      }

      const res = (viewportHeight - Math.max(top - scrollTop, 0) - Math.max(scrollBottom - bottom, 0)) / viewportHeight;

      return Math.max(Math.min(res, 1), 0);
    };

    const sortedFragments = this.fragments.sort(
      (fragmentA, fragmentB) => fragmentPartViewport(fragmentB) - fragmentPartViewport(fragmentA),
    );

    const activeFragment = sortedFragments[0];

    return !!activeFragment && activeFragment.name === name;
  }

  private getFragmentsPosition(): FragmentPosositions<T> {
    /* istanbul ignore else */
    if (!this.cachedFragmentsPositions) {
      this.cachedFragmentsPositions = this.fragments.reduce<FragmentPosositions<T>>((res, fragment) => {
        res[fragment.name] = {
          bottom: fragment.node.offsetHeight + fragment.node.offsetTop,
          top: fragment.node.offsetTop,
        };

        return res;
      }, {} as FragmentPosositions<T>);
    }
    window.clearTimeout(this.clearCacheTimeout);
    /* istanbul ignore next */
    this.clearCacheTimeout = window.setTimeout(() => {
      this.cachedFragmentsPositions = null;
    }, 100);

    return this.cachedFragmentsPositions;
  }
}
